/** @format */
import { TFunction } from 'i18next';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { LegalLinksContextProps } from '@toucan/client/global/application';
import { getHelpCentreUrl } from '@toucan/client/global/intercom';
import {
    Market,
    MarketCode,
    europeanMarkets,
    hasImpressum,
    hasModernSlaveryStatement,
    hasPlatformTermsOfUse,
} from '@toucan/client/global/market';

import { TypenameTemplatePath } from '@toucan/client/core/contentful';

import { LinkProps, LinkSize } from '@toucan/client/shared/components/link';

import { FooterLinks } from '@toucan/client/features/layout/components/footer/types';

// eslint-disable-next-line id-length -- Legacy code: do not use as an example.
const getDiscoverLinks = (t: TFunction, activeMarket: Market): FooterLinks => {
    const marketSlug = activeMarket.slug;
    const contentSlug = t('content', { ns: 'urls' });
    const aboutSlug = t('about', { ns: 'urls' });
    const careersLink = 'https://careers.mpb.com';
    const mediaLink = `info/${t('news', { ns: 'urls' })}`;
    const promosSlug = t('promos', { ns: 'urls' });
    const affiliatesLink = 'info/affiliates';

    const links = [
        {
            'data-testid': 'link__about',
            href: `/${marketSlug}/${aboutSlug}`,
            children: t('About', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__original-content',
            href: `/${marketSlug}/${contentSlug}`,
            templatePath: '/content',
            children: t('Original content', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__careers',
            href: careersLink,
            target: '_blank',
            children: t('Careers', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__media',
            href: `/${marketSlug}/${mediaLink}`,
            children: t('Media', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__promos',
            href: `/${marketSlug}/${promosSlug}`,
            children: t('Promos', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },

        {
            'data-testid': 'link__affiliates',
            href: `/${marketSlug}/${affiliatesLink}`,
            children: t('Affiliates', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
    ];

    return {
        title: t('Discover'),
        links,
    };
};

const getLegalLinks = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    activeMarket: Market,
    legalLinks?: LegalLinksContextProps
): FooterLinks => {
    const marketSlug = activeMarket.slug;
    const helpSlug = t('help', { ns: 'urls' });
    const { publicRuntimeConfig } = getConfig();
    const { californiaResidentPrivacyNoticeUrl } = publicRuntimeConfig;
    const termsSlug =
        legalLinks?.termsPage?.urlSlug ??
        `${helpSlug}/${t('terms-and-conditions', { ns: 'urls' })}`;
    const privacySlug =
        legalLinks?.privacyPage?.urlSlug ??
        `${helpSlug}/${t('privacy-policy', { ns: 'urls' })}`;
    const cookiesSlug =
        legalLinks?.cookiePage?.urlSlug ??
        `${helpSlug}/${t('cookie-policy', { ns: 'urls' })}`;
    const warrantySlug =
        legalLinks?.warrantyPage?.urlSlug ??
        `${helpSlug}/${t('warranty', { ns: 'urls' })}`;
    const modernSlaveryStatementSlug =
        legalLinks?.modernSlaveryStatement?.urlSlug ??
        `${helpSlug}/${t('modern-slavery-statement', { ns: 'urls' })}`;

    const links: LinkProps[] = [
        {
            'data-testid': 'link__terms',
            href: `/${marketSlug}/${termsSlug}`,
            children: t('Terms', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
    ];

    if (
        hasPlatformTermsOfUse(activeMarket.code) &&
        legalLinks?.platformTermsOfUsePage?.urlSlug
    ) {
        links.push({
            'data-testid': 'link__platform-terms-of-use',
            children: t('Platform Terms of Use', {
                context: 'footer-link',
            }),
            href: `/${marketSlug}/${legalLinks.platformTermsOfUsePage.urlSlug}`,
            size: LinkSize.SMALL,
        });
    }

    links.push(
        {
            'data-testid': 'link__privacy',
            href: `/${marketSlug}/${privacySlug}`,
            children: t('Privacy', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__cookies',
            href: `/${marketSlug}/${cookiesSlug}`,
            children: t('Cookies', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        }
    );

    if (activeMarket.code === MarketCode.EN_US) {
        links.push(
            {
                'data-testid': 'link__warranty',
                href: `/${marketSlug}/${warrantySlug}`,
                children: t('Warranty', { context: 'footer-link' }),
                size: LinkSize.SMALL,
            },
            {
                'data-testid': 'link__california-privacy-policy',
                href: californiaResidentPrivacyNoticeUrl,
                children: 'California Resident Privacy Notice',
                size: LinkSize.SMALL,
                target: '_blank',
            }
        );
    }

    if (hasModernSlaveryStatement(activeMarket.code)) {
        links.push({
            'data-testid': 'link__modern-slavery-act',
            href: `/${marketSlug}/${modernSlaveryStatementSlug}`,
            children: t('Modern Slavery Statement', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        });
    }

    return {
        links,
        title: t('Legal'),
    };
};

const getHelpLinks = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    activeMarket: Market,
    legalLinks?: LegalLinksContextProps,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    isConsolidatedIntercomConfigActive: boolean = false
): FooterLinks => {
    const intercomUrl = getHelpCentreUrl({
        market: activeMarket,
        isConsolidatedIntercomConfigActive,
    });
    const helpSlug = t('help', { ns: 'urls' });
    const contactSlug = t('contact-us', { ns: 'urls' });
    const companyInfoSlug =
        legalLinks?.companyInfoPage?.urlSlug ??
        `${helpSlug}/${t('company-info', { ns: 'urls' })}`;
    const marketSlug = activeMarket.slug;

    const impressumSlug =
        legalLinks?.impressumPage?.urlSlug ??
        `${helpSlug}/${t('impressum', { ns: 'urls' })}`;

    const links = [
        {
            'data-testid': 'link__faqs',
            href: intercomUrl,
            children: t('FAQs'),
            target: '_blank',
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__contact-us',
            href: `/${marketSlug}/${helpSlug}/${contactSlug}`,
            templatePath: TypenameTemplatePath.Contact,
            children: t('Contact'),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': 'link__company-info',
            href: `/${marketSlug}/${companyInfoSlug}`,
            templatePath: TypenameTemplatePath.PageHelp,
            children: t('Company Info'),
            size: LinkSize.SMALL,
        },
    ];

    if (hasImpressum(activeMarket.language)) {
        links.push({
            'data-testid': 'link__impressum',
            href: `/${marketSlug}/${impressumSlug}`,
            templatePath: TypenameTemplatePath.PageHelp,
            children: t('Impressum'),
            size: LinkSize.SMALL,
        });
    }

    return {
        title: t('Help'),
        links,
    };
};

export const getDefaultFooterLinks = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    activeMarket: Market,
    legalLinks?: LegalLinksContextProps,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    isConsolidatedIntercomConfigActive: boolean = false
) => {
    return [
        getDiscoverLinks(t, activeMarket),
        getLegalLinks(t, activeMarket, legalLinks),
        getHelpLinks(
            t,
            activeMarket,
            legalLinks,
            isConsolidatedIntercomConfigActive
        ),
    ];
};

export const getCheckoutFooterLinks = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    activeMarket: Market,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    isConsolidatedIntercomConfigActive: boolean = false
) => {
    return [
        getLegalLinks(t, activeMarket),
        getHelpLinks(
            t,
            activeMarket,
            undefined,
            isConsolidatedIntercomConfigActive
        ),
    ];
};

export const getRestrictedFooterLinks = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    activeMarket: Market,
    // eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
    showRightOfWithdrawal: boolean = false,
    legalLinks?: LegalLinksContextProps
): LinkProps[] => {
    const marketSlug = activeMarket.slug;
    const helpSlug = t('help', { ns: 'urls' });
    const contactSlug = t('contact-us', { ns: 'urls' });
    const termsSlug =
        legalLinks?.termsPage?.urlSlug ??
        `${helpSlug}/${t('terms-and-conditions', { ns: 'urls' })}`;
    const testId = `restricted-footer-${activeMarket.code.toLowerCase()}`;

    const links: LinkProps[] = [
        {
            'data-testid': `${testId}__help`,
            href: `/${marketSlug}/${helpSlug}/${contactSlug}`,
            templatePath: TypenameTemplatePath.Contact,
            children: t('Help'),
            size: LinkSize.SMALL,
        },
        {
            'data-testid': `${testId}__legal`,
            href: `/${marketSlug}/${termsSlug}`,
            templatePath: TypenameTemplatePath.PageHelp,
            children: t('Legal', { context: 'footer-link' }),
            size: LinkSize.SMALL,
        },
    ];

    if (
        hasPlatformTermsOfUse(activeMarket.code) &&
        legalLinks?.platformTermsOfUsePage?.urlSlug
    ) {
        links.push({
            'data-testid': `${testId}__platform-terms-of-use`,
            children: t('Platform Terms of Use', { context: 'footer-link' }),
            href: `/${marketSlug}/${legalLinks.platformTermsOfUsePage.urlSlug}`,
            templatePath: TypenameTemplatePath.PageHelp,
            size: LinkSize.SMALL,
            target: '_blank',
            rel: 'noopener noreferrer',
        });
    }

    // add impressum link (this is only vailable on DE language markets)
    if (hasImpressum(activeMarket.language)) {
        links.push({
            'data-testid': `${testId}__impressum`,
            href: `/${marketSlug}/${helpSlug}/impressum`,
            templatePath: TypenameTemplatePath.PageHelp,
            children: 'Impressum',
            size: LinkSize.SMALL,
        });
    }

    // add right of withdrawal links (EU markets only)
    if (showRightOfWithdrawal && europeanMarkets.includes(activeMarket.code)) {
        links.push({
            'data-testid': `${testId}__right-of-withdrawal-link`,
            href: `/${marketSlug}/${termsSlug}`,
            templatePath: TypenameTemplatePath.PageHelp,
            children: t('Right of withdrawal'),
            size: LinkSize.SMALL,
        });
    }

    return links;
};
